/**
 * Deck API — retro Express backend with Auth0 bearer tokens.
 */

import { retroAuthFetch, setRetroAuth } from '../../utils/retroAuthFetch';
import type { Deck, DeckSummary } from './types';

export function configureDeckApi(authFn: () => Promise<Record<string, string>>) {
  setRetroAuth(authFn);
}

async function deckFetch(path: string, init: RequestInit = {}) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };
  return retroAuthFetch(path, { ...init, headers });
}

export async function apiListDecks(): Promise<DeckSummary[]> {
  const res = await deckFetch('/api/decks');
  if (!res.ok) return [];
  return res.json() as Promise<DeckSummary[]>;
}

export async function apiLoadDeck(id: string): Promise<Deck | null> {
  const res = await deckFetch(`/api/decks/${encodeURIComponent(id)}`);
  if (!res.ok) return null;
  return res.json() as Promise<Deck>;
}

export async function apiSaveDeck(deck: Deck): Promise<Deck> {
  const res = await deckFetch(`/api/decks/${encodeURIComponent(deck.id)}`, {
    method: 'PUT',
    body: JSON.stringify(deck),
  });
  if (!res.ok) throw new Error(`Save failed (${res.status})`);
  return res.json() as Promise<Deck>;
}

export async function apiCreateDeck(deck: Deck): Promise<Deck> {
  const res = await deckFetch('/api/decks', {
    method: 'POST',
    body: JSON.stringify(deck),
  });
  if (!res.ok) throw new Error(`Create failed (${res.status})`);
  return res.json() as Promise<Deck>;
}

export async function apiDeleteDeck(id: string): Promise<void> {
  await deckFetch(`/api/decks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function apiCreateDeckFromImage(
  imageUrl: string,
  name?: string,
  preset = 'right-column',
): Promise<Deck> {
  const res = await deckFetch('/api/decks/from-image', {
    method: 'POST',
    body: JSON.stringify({ imageUrl, name, preset }),
  });
  if (!res.ok) {
    let msg = `Create failed (${res.status})`;
    try {
      const d = await res.json();
      if (d?.error) msg = d.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<Deck>;
}
